import { useMemo, useState } from "react";
import { ShoppingBasket, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

const CATEGORIES = [
  { id: "all", label: "All Products" },
  { id: "candles", label: "Beeswax Candles" },
  { id: "lip-balms", label: "Lip Balms" },
  { id: "soaps", label: "Soaps" },
  { id: "lotions", label: "Lotions" },
];

export function Shop({ products, loading }) {
  const [filter, setFilter] = useState("all");
  const { addItem } = useCart();

  const visible = useMemo(
    () => (filter === "all" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );

  return (
    <section id="shop" className="bg-[#FAF5EE]" data-testid="shop-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <Reveal className="max-w-xl">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#D97706] font-semibold" data-testid="shop-overline">The Shop</p>
          <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2C221E] tracking-tight" data-testid="shop-heading">
            From our hives to your home
          </h2>
          <p className="mt-4 text-base text-[#5A4D44] leading-relaxed" data-testid="shop-subtext">
            While we wait on the honey, the bees keep us well supplied with wax. Everything here is
            handcrafted by our family in small batches with pure beeswax and honey.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-2.5" y={16}>
          <div className="flex flex-wrap gap-2.5" data-testid="shop-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`shop-filter-${c.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors active:scale-[0.97] ${
                  filter === c.id
                    ? "bg-[#2C221E] text-[#FAF5EE]"
                    : "bg-white border border-[#E6D7C3] text-[#5A4D44] hover:border-[#D97706] hover:text-[#B45309]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <p className="mt-16 text-center text-[#8C7A6B]" data-testid="shop-loading">Loading the goods...</p>
        ) : visible.length === 0 ? (
          <div className="mt-16 text-center py-16 bg-white rounded-2xl border border-dashed border-[#E6D7C3]" data-testid="shop-empty-category">
            <Package className="w-10 h-10 text-[#E6D7C3] mx-auto" />
            <p className="mt-4 font-serif-display text-xl font-semibold text-[#2C221E]">Nothing here just yet</p>
            <p className="mt-2 text-sm text-[#8C7A6B] max-w-md mx-auto">
              We're still perfecting this batch in the workshop. Check back soon — or try another category.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="shop-grid">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <article
                  className="group h-full bg-white rounded-2xl border border-[#E6D7C3] overflow-hidden hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300"
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="relative overflow-hidden bg-[radial-gradient(circle_at_50%_28%,#FFFFFF_0%,#F3EBE0_78%)]">
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      data-testid={`product-image-${p.id}`}
                    />
                    {p.badge && (
                      <span className="absolute top-4 left-4 bg-[#EDE0CF] text-[#78350F] text-xs font-semibold px-3 py-1.5 rounded-full" data-testid={`product-badge-${p.id}`}>
                        {p.badge}
                      </span>
                    )}
                    {p.stock === 0 && (
                      <span className="absolute top-4 right-4 bg-[#2C221E] text-white text-xs font-semibold px-3 py-1.5 rounded-full" data-testid={`product-soldout-${p.id}`}>
                        Sold Out
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif-display text-xl font-semibold text-[#2C221E]" data-testid={`product-name-${p.id}`}>{p.name}</h3>
                      <div className="text-right">
                        <span className="font-serif-display text-lg font-bold text-[#B45309] whitespace-nowrap block" data-testid={`product-price-${p.id}`}>
                          ${p.price.toFixed(2)}
                        </span>
                        <span className="text-[11px] font-medium text-[#2D4A3E] whitespace-nowrap" data-testid={`product-shipping-${p.id}`}>Shipping included</span>
                      </div>
                    </div>
                    <p className="mt-2.5 text-sm text-[#5A4D44] leading-relaxed line-clamp-3" data-testid={`product-description-${p.id}`}>{p.description}</p>
                    <button
                      onClick={() => addItem(p)}
                      disabled={p.stock === 0}
                      data-testid={`add-to-cart-${p.id}`}
                      className="mt-5 w-full py-3 rounded-full bg-[#2C221E] text-[#FAF5EE] hover:bg-[#D97706] font-semibold text-sm transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ShoppingBasket className="w-4 h-4" />
                      {p.stock === 0 ? "Sold Out" : "Add to Basket"}
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.1}>
          <div className="mt-14 bg-[#F3EBE0] rounded-2xl p-8 border border-[#E6D7C3]" data-testid="shop-coming-note">
            <p className="font-serif-display text-lg font-semibold text-[#2C221E]">More goodness in the works</p>
            <p className="mt-2 text-sm text-[#5A4D44] leading-relaxed max-w-2xl">
              Lip balms, soaps, and lotions made with our beeswax and honey are being perfected in the family
              workshop right now — they'll land here as soon as they're ready. And of course: raw McGee's Bees
              honey, coming with our first harvest in Spring 2027.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
